import { getPlate } from '../data/plates';
import { activeTrip, type PlayerState, type TripState } from './types';

/**
 * The single claim entry point. Everything the UI does to record a spotted
 * plate goes through here, which is what keeps the two-ledger rule honest:
 *
 *   | Situation                        | Trip score | Lifetime score |
 *   |----------------------------------|------------|----------------|
 *   | New plate, no trip running       |     -      |    +points     |
 *   | New plate, during a trip         |  +points   |    +points     |
 *   | Already collected, during a trip |  +points   |       -        |
 *   | Already claimed this trip        |     -      |       -        |
 *
 * Claim-once is enforced as a data constraint - claims are sets keyed by plate
 * id - rather than as a check scattered across screens.
 */

export interface ClaimOutcome {
  state: PlayerState;
  /** True if this claim added to the active trip's score. */
  creditedTrip: boolean;
  /** True if this plate was new to the lifetime collection. */
  creditedLifetime: boolean;
  /** The plate's point value, or 0 if nothing was credited. */
  points: number;
}

function withoutKey(map: Record<string, number>, key: string): Record<string, number> {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
}

function replaceTrip(state: PlayerState, updated: TripState): TripState[] {
  return state.trips.map((t) => (t.id === updated.id ? updated : t));
}

/** Is this plate currently claimable in whichever ledger is active? */
export function canClaim(state: PlayerState, plateId: string): boolean {
  if (!getPlate(plateId)) return false;
  const trip = activeTrip(state);
  return trip ? !(plateId in trip.myClaims) : !(plateId in state.lifetime);
}

export function claim(state: PlayerState, plateId: string, now = Date.now()): ClaimOutcome {
  const plate = getPlate(plateId);
  if (!plate) {
    return { state, creditedTrip: false, creditedLifetime: false, points: 0 };
  }

  const trip = activeTrip(state);
  let next = state;
  let creditedTrip = false;
  let creditedLifetime = false;

  if (trip && !(plateId in trip.myClaims)) {
    const updatedTrip: TripState = {
      ...trip,
      myClaims: { ...trip.myClaims, [plateId]: now },
      undone: withoutKey(trip.undone, plateId),
      version: trip.version + 1,
    };
    next = { ...next, trips: replaceTrip(next, updatedTrip) };
    creditedTrip = true;
  }

  if (!(plateId in state.lifetime)) {
    next = {
      ...next,
      lifetime: { ...next.lifetime, [plateId]: now },
      lifetimeUndone: withoutKey(next.lifetimeUndone, plateId),
    };
    creditedLifetime = true;
  }

  if (!creditedTrip && !creditedLifetime) {
    return { state, creditedTrip: false, creditedLifetime: false, points: 0 };
  }

  return {
    state: { ...next, version: next.version + 1 },
    creditedTrip,
    creditedLifetime,
    points: plate.points,
  };
}

/**
 * Undo mirrors whatever the matching claim credited.
 *
 * A trip claim only rolls back the lifetime entry if that same claim created it
 * - matched on the timestamp the two were written with. Undoing today's claim
 * must not erase a plate collected on a trip last year.
 */
export function undoClaim(state: PlayerState, plateId: string, now = Date.now()): PlayerState {
  const trip = activeTrip(state);
  let next = state;
  let changed = false;

  if (trip && plateId in trip.myClaims) {
    const claimedAt = trip.myClaims[plateId];
    const updatedTrip: TripState = {
      ...trip,
      myClaims: withoutKey(trip.myClaims, plateId),
      undone: { ...trip.undone, [plateId]: now },
      version: trip.version + 1,
    };
    next = { ...next, trips: replaceTrip(next, updatedTrip) };
    changed = true;

    // Only strip the lifetime entry if this very claim established it.
    if (next.lifetime[plateId] === claimedAt) {
      next = {
        ...next,
        lifetime: withoutKey(next.lifetime, plateId),
        lifetimeUndone: { ...next.lifetimeUndone, [plateId]: now },
      };
    }
  } else if (!trip && plateId in state.lifetime) {
    next = {
      ...next,
      lifetime: withoutKey(next.lifetime, plateId),
      lifetimeUndone: { ...next.lifetimeUndone, [plateId]: now },
    };
    changed = true;
  }

  return changed ? { ...next, version: next.version + 1 } : state;
}
